import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Plus } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Motion";
import { useLang } from "../lib/lang";
import { WHATSAPP_NUMBER } from "../lib/content";

export default function Faqs() {
  const { t } = useLang();
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div>
      <Seo title={t.meta.faqs.title} description={t.meta.faqs.desc} />

      <section className="mx-auto max-w-3xl px-5 pb-20 pt-12 sm:pt-16" data-testid="faqs-page">
        <Reveal y={12}>
          <p className="eyebrow">{t.faqs.eyebrow}</p>
          <h1 className="mt-4 font-display text-4xl leading-tight text-ink sm:text-5xl">{t.faqs.title}</h1>
          <p className="mt-5 text-base leading-relaxed text-[#475569]">{t.faqs.sub}</p>
        </Reveal>

        <div className="mt-12 space-y-4">
          {t.faqs.items.map((f, i) => {
            const open = openIdx === i;
            return (
              <Reveal key={i} delay={0.04 * i}>
                <div
                  className={`card-warm overflow-hidden transition-colors ${open ? "bg-[#FDFBF7]" : ""}`}
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    aria-expanded={open}
                    data-testid={`faq-toggle-${i}`}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-semibold text-ink">{f.q}</span>
                    <motion.span
                      animate={{ rotate: open ? 45 : 0 }}
                      transition={{ duration: 0.25 }}
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${open ? "bg-saffron text-[#FDFBF7]" : "bg-[#FFEDD5] text-saffron"}`}
                    >
                      <Plus size={16} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        data-testid={`faq-answer-${i}`}
                      >
                        <p className="px-6 pb-6 text-[15px] leading-relaxed text-[#475569]">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14 rounded-[2rem] bg-ink px-8 py-10 text-center" data-testid="faqs-still-card">
            <h2 className="font-display text-2xl text-cream sm:text-3xl">{t.faqs.stillTitle}</h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-cream/75">{t.faqs.stillText}</p>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="faqs-whatsapp-cta"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-saffron px-7 py-3.5 text-sm font-semibold text-[#FDFBF7] transition-all duration-300 hover:-translate-y-0.5 hover:bg-cream hover:text-ink"
            >
              <MessageCircle size={17} />
              {t.faqs.stillCta}
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
